import { lazy, Suspense, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Storage } from "react-jhipster";
import { connect } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { useHistory } from "./router/useHistory";
import PrivateRoute from "./components/private-route/private-route";
import { canAccessTheRoutes, logoutUserWithNoAccess } from "./helper/helperFunctions";
import VerifyAccount from "./pages/private/verify-account";
import { logout } from "./redux/reducers/authentication/authentication";
import { getAssignedFormsForUser, getForms } from "./redux/reducers/form/form.reducer";

// Day 21.1 — code split every page so first-paint bundle isn't a 9MB monolith.
// PrivateRoute (wrapper) and VerifyAccount (rendered outside <Routes>) stay eager.
const Login = lazy(() => import("./pages/auth/login"));
const PasswordResetChange = lazy(() => import("./pages/auth/passwordResetChange"));
const PasswordResetMessageEmail = lazy(() => import("./pages/auth/passwordResetMessageEmail"));
const PasswordResetMessageSuccess = lazy(() => import("./pages/auth/passwordResetMessageSuccess"));
const PasswordResetRequest = lazy(() => import("./pages/auth/passwordResetRequest"));
const RegisterDevice = lazy(() => import("./pages/auth/register-device"));
const SetupAccount = lazy(() => import("./pages/auth/setup-account"));
const Signup = lazy(() => import("./pages/auth/signup"));
const PageNotFound = lazy(() => import("./pages/error/PageNotFound"));
const FormPage = lazy(() => import("./pages/private/form/form-page"));
const InvoicePdfView = lazy(() => import("./pages/private/management/company/subscriptions/invoices/invoice-pdf-view"));
const SmsVerification = lazy(() => import("./pages/private/management/company/subscriptions/sms/sms-verification"));
const SubscriptionVerification = lazy(() => import("./pages/private/management/company/subscriptions/subscription-verification-modal"));
const ManagementPage = lazy(() => import("./pages/private/management/management-page"));
const ReportsPage = lazy(() => import("./pages/private/reports/reports-page"));
const ReportPdfView = lazy(() => import("./pages/private/reports/reports-pdf-view"));
const TermsAndConditions = lazy(() => import("./pages/public/TermsAndConditions"));
const AnalyticsMain = lazy(() => import("./pages/private/analytics/main-page/analytics-main"));
const PlanDialog = lazy(() => import("./components/dialogs/PlanDialog"));
// PascalCase: react-router v7 renders routes via `element={<X/>}`; a lowercase
// JSX tag would be treated as a DOM element, not a component.
const CompanyUsersProfile = lazy(() => import("./pages/private/management/company/users/company-users-profile"));
const UpdatePlan = lazy(() => import("./pages/private/management/company/subscriptions/UpdatePlan"));
const CompanyUsersAdd = lazy(() => import("./pages/private/management/company/users/company-users-add"));
const CompanySitesAdd = lazy(() => import("./pages/private/management/company/sites/company-sites-add"));
const RolesEditDialog = lazy(() => import("./pages/private/management/company/roles/RolesEditDialog"));
const HomeWrapper = lazy(() => import("./pages/public/homepage/HomeWrapper"));
const PrivacyPolicy = lazy(() => import("./pages/public/PrivacyPolicy"));

const RouteFallback = () => (
  <div className="flex justify-center items-center h-screen w-full">
    <div className="w-10 h-10 border-4 border-colPrimary border-t-transparent rounded-full animate-spin" />
  </div>
);

const AppRoutes = ({
  user,
  logout,
  getForms,
  currentRole,
  rolesAccess,
  isAuthenticated,
  getAssignedFormsForUser,
  // ,
}) => {
  const history = useHistory();
  const [laterActivate, setLaterActivate] = useState(false);
  const activateLater = Storage.local.get("activate");

  useEffect(() => {
    if (!currentRole) return;
    // Day 21.9 — only fetch the forms LIST on routes that need it.
    // Form DETAIL routes (`/forms/form-management/{id}`) don't display the list,
    // so the LIST call was wasted (~195ms per open). Same for /reports/*,
    // /management/*, /analytics. Gating here saves cumulative ~200ms on every
    // detail-page load and ~1MB of avoided JSON payload over a typical session.
    // Audited via Playwright MCP 2026-05-08.
    const path = window.location.pathname;
    const needsFormsList =
      path === "/forms" ||
      path === "/forms/form-management" ||
      path === "/forms/archive" ||
      path === "/forms/library" ||
      path === "/forms/global-list";
    if (!needsFormsList) return;

    if (currentRole === "user") {
      getAssignedFormsForUser({
        limit: 10,
        page: 1,
        sortBy: "formUpdatedAt:desc",
      });
    } else if (path === "/forms/archive") {
      getForms({
        sortBy: "updatedAt:desc",
        isArchived: true,
      });
    } else {
      getForms({
        limit: 10,
        page: 1,
        sortBy: "updatedAt:desc",
        isArchived: false,
      });
    }
    // eslint-disable-next-line
  }, [currentRole]);

  useEffect(() => {
    if (user?.subscription?.name === "basic" && user?.subscription?.isRemainingTrails <= 0 && user?.subscription?.isRemainingTrails !== null) {
      toast.error(`Your remaining trails is ${user?.subscription?.isRemainingTrails}`);
    }
  }, [user?.subscription, user?.role]);

  useEffect(() => {
    // checking if user have routes access
    if (user?.roleSetting && !user?.roleSetting?.managerRole && !user?.roleSetting?.inspectorRole) {
      logoutUserWithNoAccess(logout, history);
    } else if (user?.roleSetting?.managerRole && rolesAccess) {
      canAccessTheRoutes(rolesAccess, logout, history);
    }
    // eslint-disable-next-line
  }, [user, rolesAccess]);

  if (user) {
    localStorage.setItem("language", user?.language);
    if (!user.isEmailVerified && !activateLater && laterActivate === false) {
      return <VerifyAccount setLaterActivate={setLaterActivate} />;
    }
    // if (!user.company) {
    //   return <CompanySetup />;
    // }
  }

  // admin / superAdmin / manager get the two subscription modal routes;
  // regular users do not (mirrors the legacy role arrays in the v5 <Switch>).
  const isAdminLike =
    !!user && (currentRole === "admin" || currentRole === "superAdmin" || currentRole === "manager");

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* report PDF web/email view. The legacy `/report-pdf-view/:id/view`
            route was dead in v5 (the `:id/:email` route is listed first and
            shadows it — `email` = "view"); removed so v7's rank-based matching
            keeps the same behavior instead of flipping to the view={true} path. */}
        <Route path="/report-pdf-view/:id/:email" element={<ReportPdfView />} />
        <Route path="/management/subscriptions/add-payment-method-modal" element={<PlanDialog />} />
        <Route path="/management/subscriptions/update-plan" element={<UpdatePlan />} />
        <Route path="/password-reset-message-success" element={<PasswordResetMessageSuccess />} />
        <Route path="/password-reset-message-email" element={<PasswordResetMessageEmail />} />
        <Route path="/reset_password/:token" element={<PasswordResetChange />} />
        <Route path="/password-reset-request" element={<PasswordResetRequest />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/homePage" element={<HomeWrapper />} />
        <Route path="/invoice/:id" element={<InvoicePdfView />} />
        <Route path="/login" element={<Login />} />
        <Route path="/setup-account/:token" element={<SetupAccount />} />
        <Route path="/register-device/:token" element={<RegisterDevice />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/company-user-profile" element={<CompanyUsersProfile />} />
        <Route path="/management/users/:id/edit" element={<CompanyUsersAdd />} />
        <Route path="/management/sites/:id/edit" element={<CompanySitesAdd />} />
        <Route path="/management/roles/:id/edit" element={<RolesEditDialog />} />
        <Route path="/management/roles/add" element={<RolesEditDialog />} />
        <Route path="/404" element={<PageNotFound />} />

        <Route path="/" element={<Navigate to="/forms/form-management" replace />} />
        <Route path="/analytics" element={<PrivateRoute component={AnalyticsMain} isAuthenticated={isAuthenticated} />} />

        {isAdminLike && (
          <Route
            path="/subscription-payment"
            element={
              <SubscriptionVerification
                showDlg={true}
                secondaryAction={() => {
                  history.push("/");
                }}
              />
            }
          />
        )}
        {isAdminLike && (
          <Route
            path="/subscriptions"
            element={
              <SmsVerification
                showDlg={true}
                secondaryAction={() => {
                  history.push("/");
                }}
              />
            }
          />
        )}

        {/* Section routers. The legacy regex `:moduleName(...)` param (removed in
            react-router v6+) acted as a module whitelist + role gate; that gating
            now lives inside FormPage / ReportsPage / ManagementPage, which render
            their own nested <Routes>. The `/*` splats let those nested routes
            match. The specific routes above out-rank these splats. */}
        <Route path="/forms/*" element={<PrivateRoute component={FormPage} isAuthenticated={isAuthenticated} />} />
        <Route path="/reports/*" element={<PrivateRoute component={ReportsPage} isAuthenticated={isAuthenticated} />} />
        <Route path="/management/*" element={<PrivateRoute component={ManagementPage} isAuthenticated={isAuthenticated} />} />
      </Routes>
    </Suspense>
  );
};

const mapStateToProps = ({ authentication }) => ({
  user: authentication.user,
  currentRole: authentication.currentRole,
  rolesAccess: authentication.userRoleAccess,
  isAuthenticated: authentication.isAuthenticated,
});

const mapDispatchToProps = { logout, getForms, getAssignedFormsForUser };

export default connect(mapStateToProps, mapDispatchToProps)(AppRoutes);
